import { useState, useEffect, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { RiInformationLine } from "react-icons/ri";
import { FiCheckCircle } from "react-icons/fi";
import { FiXCircle } from "react-icons/fi";
import { MdSettings, MdAttachFile } from "react-icons/md";
import FileUploadCard from "../file-upload-card";
import { transformBackendAttachments } from "../utils/attachment-transformer";
import { FileAttachment } from "~/stores/slices/connection-attachments";
import { useGlobalStore } from "~/stores/store-provider";

interface PostgreSQLConnectionForm {
  connectionName: string;
  user: string;
  password: string;
  host: string;
  port: number;
  database: string;
}

interface PostgreSQLEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PostgreSQLEditModal = ({
  open,
  onOpenChange,
}: PostgreSQLEditModalProps) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  const [activeTab, setActiveTab] = useState<"connection" | "attachments">(
    "connection"
  );
  const [formData, setFormData] = useState<PostgreSQLConnectionForm>({
    connectionName: "",
    user: "",
    password: "",
    host: "",
    port: 5432,
    database: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isCalloutExiting, setIsCalloutExiting] = useState<boolean>(false);
  const {
    connectionDetails,
    updateConnection,
    attachments,
    setAttachments,
    deleteNewAttachments,
    resetAttachments,
  } = useGlobalStore((state) => state);

  // Handle dialog open/close with native methods
  useEffect(() => {
    if (open && modalRef.current) {
      if (!connectionDetails) return;
      modalRef.current.showModal();
      setFormData({
        connectionName: connectionDetails.name,
        user: connectionDetails.username,
        password: "",
        host: connectionDetails.host,
        port: connectionDetails.port,
        database: connectionDetails.database,
      });

      // Initialize attachments in the Zustand slice
      const transformedAttachments = transformBackendAttachments(
        connectionDetails.attachments || []
      );
      setAttachments(transformedAttachments);
    } else if (!open && modalRef.current) {
      setIsLoading(false);
      setIsConnected(null);
      setIsCalloutExiting(false);
      setActiveTab("connection"); // Reset to connection tab

      // Delete newly uploaded attachments when modal closes without saving
      deleteNewAttachments().catch((error: any) => {
        console.error("Failed to cleanup new attachments:", error);
      });

      resetAttachments();
      modalRef.current.close();
    }
  }, [open, connectionDetails, setAttachments]);

  const closeModal = () => {
    modalRef.current?.close();
    onOpenChange(false);
  };

  const handleInputChange = (
    field: keyof PostgreSQLConnectionForm,
    value: string | number
  ) => {
    setFormData({
      ...formData,
      [field]: field === "port" ? Number(value) : value,
    });
  };

  const isFormValid = () => {
    return (
      formData.connectionName.trim() !== "" &&
      formData.user.trim() !== "" &&
      formData.host.trim() !== "" &&
      formData.port > 0 &&
      formData.port <= 65535 &&
      formData.database.trim() !== ""
    );
  };

  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      // Transform attachments from Zustand slice to match API structure
      const attachmentsData = attachments.map((attachment: FileAttachment) => ({
        description: attachment.description,
        file_id: attachment.fileId,
        file_name: attachment.fileName,
      }));

      const updateData = {
        type: "postgresql",
        username: formData.user,
        password: formData.password,
        host: formData.host,
        port: formData.port,
        database: formData.database,
        connectionName: formData.connectionName,
        attachments: attachmentsData,
      };
      await updateConnection(connectionDetails._id, updateData);

      setIsConnected(true);
      setIsCalloutExiting(false);
      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 2700);

      setTimeout(() => {
        onOpenChange(false);
      }, 2000);
    } catch {
      setIsLoading(false);
      setIsConnected(false);
      setIsCalloutExiting(false);
      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 4000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Edit PostgreSQL Connector</h3>
          <button
            className="btn btn-sm btn-circle btn-ghost"
            onClick={closeModal}
          >
            <IoClose />
          </button>
        </div>

        {/* Tabs */}
        <div role="tablist" className="tabs tabs-border mb-4">
          <a
            role="tab"
            className={`tab ${activeTab === "connection" ? "tab-active" : ""}`}
            onClick={() => setActiveTab("connection")}
          >
            <MdSettings className="size-4 me-2" />
            Connection Details
          </a>
          <a
            role="tab"
            className={`tab ${activeTab === "attachments" ? "tab-active" : ""}`}
            onClick={() => setActiveTab("attachments")}
          >
            <MdAttachFile className="size-4 me-2" />
            Attachments ({attachments.length})
          </a>
        </div>

        {/* Tab Content */}
        {activeTab === "connection" && (
          <>
            <div className="alert bg-blue-400/20 text-blue-300 mb-4">
              <RiInformationLine className="h-5 w-5" />
              <div>
                <h4 className="font-bold">Editing Connection</h4>
                <div className="text-sm">
                  All fields will be updated with whatever you enter below. You
                  must re-enter sensitive fields
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {/* Connection Name */}
              <div>
                <label className="label">
                  <div className="flex items-center gap-1">
                    <span className="label-text font-medium">
                      Connection Name
                    </span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="Enter a name for this connection"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </div>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full mt-2"
                  placeholder="Enter connection name"
                  value={formData.connectionName}
                  onChange={(e) =>
                    handleInputChange("connectionName", e.target.value)
                  }
                />
              </div>

              {/* User */}
              <div>
                <label className="label">
                  <div className="flex items-center gap-1">
                    <span className="label-text font-medium">
                      User <span className="text-red-500">*</span>
                    </span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="Your PostgreSQL username"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </div>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full mt-2"
                  placeholder="Enter your PostgreSQL username"
                  value={formData.user}
                  onChange={(e) => handleInputChange("user", e.target.value)}
                />
              </div>

              {/* Password */}
              <div>
                <label className="label">
                  <div className="flex items-center gap-1">
                    <span className="label-text font-medium">
                      Password <span className="text-red-500">*</span>
                    </span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="Your PostgreSQL password, set when you created the database or can be reset in your database settings."
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </div>
                </label>
                <input
                  type="password"
                  className="input input-bordered w-full mt-2"
                  placeholder="Enter new password (leave blank to keep current)"
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                />
              </div>

              {/* Host */}
              <div>
                <label className="label">
                  <div className="flex items-center gap-1">
                    <span className="label-text font-medium">
                      Host <span className="text-red-500">*</span>
                    </span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="Your PostgreSQL server hostname or IP address"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </div>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full mt-2"
                  placeholder="localhost or server hostname"
                  value={formData.host}
                  onChange={(e) => handleInputChange("host", e.target.value)}
                />
              </div>

              {/* Port */}
              <div>
                <label className="label">
                  <div className="flex items-center gap-1">
                    <span className="label-text font-medium">
                      Port <span className="text-red-500">*</span>
                    </span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="PostgreSQL server port number (usually 5432)"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </div>
                </label>
                <input
                  type="number"
                  className="input input-bordered w-full mt-2"
                  placeholder="5432"
                  value={formData.port}
                  onChange={(e) => handleInputChange("port", e.target.value)}
                />
              </div>

              {/* Database */}
              <div>
                <label className="label">
                  <div className="flex items-center gap-1">
                    <span className="label-text font-medium">
                      Database <span className="text-red-500">*</span>
                    </span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="PostgreSQL database name"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </div>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full mt-2"
                  placeholder="Enter database name"
                  value={formData.database}
                  onChange={(e) =>
                    handleInputChange("database", e.target.value)
                  }
                />
              </div>

              <button
                className="mt-2 btn btn-neutral bg-blue-600 text-white hover:bg-blue-500"
                onClick={handleUpdate}
                disabled={!isFormValid() || isLoading}
              >
                {isLoading ? (
                  <span className="loading loading-spinner"></span>
                ) : (
                  "Update & Test Connection"
                )}
              </button>

              {isConnected !== null && (
                <div
                  className={`alert mt-1 ${
                    isConnected ? "alert-success" : "alert-error"
                  } ${
                    isCalloutExiting
                      ? "opacity-0 -translate-y-2"
                      : "opacity-100 translate-y-0"
                  } transition-all duration-300 ease-in-out`}
                >
                  {isConnected ? (
                    <FiCheckCircle className="h-6 w-6" />
                  ) : (
                    <FiXCircle className="h-6 w-6" />
                  )}
                  <span>
                    {isConnected
                      ? "Connection updated successfully"
                      : "Connection update failed"}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Attachments Tab Content */}
        {activeTab === "attachments" && <FileUploadCard />}
      </div>
    </dialog>
  );
};

export default PostgreSQLEditModal;
